import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DataTableItem } from 'src/app/shared/components/data-table/client-side-data-table/client-side-data-table.component';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { Store } from '@ngrx/store';
import { ThreeDotsMenuPipe } from 'src/app/shared/pipes/three-dots-menu.pipe';
import { TranslocoService } from '@ngneat/transloco';
import { WorkspacesListEventsService } from './workspaces-list-events.service';
import { BsModalService } from 'ngx-bootstrap/modal';
import { EventsServiceAction, EventsServiceData } from 'src/app/core/services/base-event-service';
import { dataTableEvents } from 'src/app/core/constants/data-table-events';
import { PermissionResource } from 'src/app/core/constants/permissions.constants';
import { PermissionService } from 'src/app/core/services/permission.service';
import { WorkspaceDto } from '../../models/workspaces.model';
import { selectWorkspaces } from '../../store/selectors';
import { WorkspacesActions } from '../../store/actions';
import { RenameWorkspaceModalComponent } from '../modals/rename-workspace-modal/rename-workspace-modal.component';
import { DateShortPipe } from 'src/app/shared/pipes/date-short.pipe';
import { Router } from '@angular/router';

@UntilDestroy()
@Component({
  selector: 'app-workspaces-list',
  templateUrl: './workspaces-list.component.html',
  styleUrl: './workspaces-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkspacesListComponent implements OnInit {
  public data: WorkspaceDto[] | undefined;
  public columns!: DataTables.ColumnSettings[];
  public sort!: (string | number)[][];
  public dataTableMenuItems!: DataTableItem[];
  public refreshDataTable: BehaviorSubject<any> = new BehaviorSubject(null);
  private canWriteWorkspaces = false;
  private canDeleteWorkspaces = false;

  constructor(
    private readonly store$: Store,
    public threeDotsMenuPipe: ThreeDotsMenuPipe,
    private translocoService: TranslocoService,
    private cdr: ChangeDetectorRef,
    public workspacesListEventsService: WorkspacesListEventsService,
    public bsModalService: BsModalService,
    public dateShortPipe: DateShortPipe,
    private readonly router: Router,
    private readonly permissionService: PermissionService,
  ) {}

  ngOnInit(): void {
    this.initSubscriptions();
    this.fetchData();
  }

  fetchData(): void {
    combineLatest([
      this.store$.select(selectWorkspaces),
      this.permissionService.canWrite$(PermissionResource.Workspaces),
      this.permissionService.canDelete$(PermissionResource.Workspaces),
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([data, canWrite, canDelete]) => {
        this.canWriteWorkspaces = canWrite;
        this.canDeleteWorkspaces = canDelete;
        this.initTable(data);
        this.cdr.detectChanges();
        setTimeout(() => {
          this.refreshDataTable.next(this.columns);
          this.cdr.detectChanges();
        }, 1);
      });
  }

  initSubscriptions(): void {
    this.translocoService.langChanges$.pipe(untilDestroyed(this)).subscribe(() => {
      if (this.data) {
        this.initTable(this.data);
        this.refreshDataTable.next(this.columns);
      }
    });

    this.workspacesListEventsService
      .getSubject()
      .pipe(untilDestroyed(this))
      .subscribe((data: EventsServiceData) => {
        switch (data.action) {
          case EventsServiceAction.View: {
            this.view(data.id);
            break;
          }
          case EventsServiceAction.NameClick: {
            this.view(data.id);
            break;
          }
          case EventsServiceAction.Edit: {
            if (!this.canWriteWorkspaces) {
              return;
            }

            this.edit(data.id);
            break;
          }
          case EventsServiceAction.Delete: {
            if (!this.canDeleteWorkspaces) {
              return;
            }

            this.delete(data.id);
            break;
          }
        }
      });
  }

  view(id: string): void {
    this.router.navigate(['/workspaces', id]);
  }

  edit(id: string): void {
    if (!this.canWriteWorkspaces) {
      return;
    }

    this.bsModalService.show(RenameWorkspaceModalComponent, {
      initialState: {
        workspace: this.data?.find((workspace) => workspace.id === id),
      },
      class: 'modal-lg',
    });
  }

  delete(id: string): void {
    if (!this.canDeleteWorkspaces) {
      return;
    }

    this.store$.dispatch(WorkspacesActions.deleteWorkspace({ id }));
  }

  initColumns(): void {
    const that = this;
    this.columns = Object.assign([
      {
        title: this.translocoService.translate('workspaces.workspaceName'),
        data: 'name',
        render: function (data: any, type: any, full: any) {
          return `<a name-click='${full.id}'>${data}</a>`;
        },
      },
      {
        title: this.translocoService.translate('columns.createdAt'),
        data: 'createdAt',
        render: {
          display: function (data: any, type: any, full: any) {
            const date = that.dateShortPipe.transform(data);
            return `<span title="${full.lastModifiedBy}"> ${date} </span>`;
          },
          sort: function (data: any, type: any, full: any) {
            return new Date(data).getTime();
          },
        },
      },
      {
        title: this.translocoService.translate('columns.updatedAt'),
        data: 'updatedAt',
        render: {
          display: function (data: any, type: any, full: any) {
            const date = that.dateShortPipe.transform(data);
            return `<span title="${full.lastModifiedBy}"> ${date} </span>`;
          },
          sort: function (data: any, type: any, full: any) {
            return new Date(data).getTime();
          },
        },
      },
      {
        width: '50px',
        data: 'id',
        searchable: false,
        orderable: false,
        render: function (data: any, type: any, full: any) {
          return that.threeDotsMenuPipe.transform(data, that.dataTableMenuItems);
        },
      },
    ]);
  }

  initTable(data: WorkspaceDto[] | null): void {
    this.initColumns();
    this.dataTableMenuItems = [
      {
        title: this.translocoService.translate('workspaces.openEdit'),
        event: dataTableEvents.common.view,
        icon: 'quick_reference_all',
      },
      ...(this.canWriteWorkspaces
        ? [
      {
        title: this.translocoService.translate('workspaces.renameWorkspace'),
        event: dataTableEvents.common.edit,
        icon: 'edit_square',
      },
          ]
        : []),
      ...(this.canDeleteWorkspaces
        ? [
      {
        title: this.translocoService.translate('actions.remove'),
        event: dataTableEvents.common.delete,
        icon: 'delete',
        disabled: false,
      },
          ]
        : []),
    ];
    this.sort = [[1, 'asc']];
    this.data = data || [];
  }
}
